/**
 * Reminder times are wall-clock values in the reminder's own zone
 * ("2026-09-19T19:00"), exactly what the API stores and the owner reads. All
 * arithmetic here stays on that naive form, so the device zone never leaks in.
 */
export type LocalDateTime = string;

const PARTS = new Map<string, Intl.DateTimeFormat>();

function formatterFor(timeZone: string): Intl.DateTimeFormat {
  const cached = PARTS.get(timeZone);
  if (cached) return cached;
  const create = (zone: string) =>
    new Intl.DateTimeFormat("en-CA", {
      timeZone: zone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hourCycle: "h23",
    });
  let format: Intl.DateTimeFormat;
  try {
    format = create(timeZone);
  } catch {
    // An unknown zone name must not break rendering; UTC is the neutral choice.
    format = create("UTC");
  }
  PARTS.set(timeZone, format);
  return format;
}

/** The wall clock in `timeZone` right now, to the minute. */
export function localNow(
  timeZone: string,
  now: Date = new Date(),
): LocalDateTime {
  const format = formatterFor(timeZone);
  const parts = format.formatToParts(now);
  const part = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((item) => item.type === type)?.value ?? "00";
  return `${part("year")}-${part("month")}-${part("day")}T${part("hour")}:${part("minute")}`;
}

function asUtc(value: LocalDateTime): Date {
  return new Date(`${value}:00Z`);
}

function fromUtc(value: Date): LocalDateTime {
  return value.toISOString().slice(0, 16);
}

function addMinutes(value: LocalDateTime, minutes: number) {
  return fromUtc(new Date(asUtc(value).getTime() + minutes * 60_000));
}

function at(value: LocalDateTime, dayOffset: number, hour: number) {
  const day = asUtc(value);
  day.setUTCDate(day.getUTCDate() + dayOffset);
  day.setUTCHours(hour, 0, 0, 0);
  return fromUtc(day);
}

/** The three one-tap reschedule targets from the design. */
export function quickPicks(now: LocalDateTime) {
  const evening = at(now, 0, 19);
  return {
    inHour: addMinutes(now, 60),
    evening: evening > now ? evening : at(now, 1, 19),
    tomorrow: at(now, 1, 9),
  };
}

const NO_TIME = "—";

const LOCAL_DATE_TIME = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/;

/** "HH:MM" of a local time, or an em dash when the value has no time part. */
export function clockLabel(value: LocalDateTime | undefined): string {
  return value && LOCAL_DATE_TIME.test(value) ? value.slice(11, 16) : NO_TIME;
}

/** "Сегодня", "Завтра" or "5 сентября" for the date part of a local time. */
export function dayLabel(
  locale: string,
  value: LocalDateTime | undefined,
  now: LocalDateTime,
): string {
  const target = asUtc(`${(value ?? "").slice(0, 10)}T00:00`);
  const base = asUtc(`${now.slice(0, 10)}T00:00`);
  if (Number.isNaN(target.getTime()) || Number.isNaN(base.getTime()))
    return NO_TIME;
  const days = Math.round((target.getTime() - base.getTime()) / 86_400_000);
  if (Math.abs(days) <= 1) {
    const text = new Intl.RelativeTimeFormat(locale, {
      numeric: "auto",
    }).format(days, "day");
    return text.charAt(0).toLocaleUpperCase(locale) + text.slice(1);
  }
  return new Intl.DateTimeFormat(locale, {
    day: "numeric",
    month: "long",
    timeZone: "UTC",
  }).format(target);
}
